import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { ArrowLeft, Mail, CheckCircle, KeyRound } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [step, setStep] = useState(1); // 1: email, 2: code + new password
  const navigate = useNavigate();

  const handleRequestReset = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Please enter your email');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API}/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      const data = await response.json();
      
      if (response.ok) {
        toast.success('Reset code sent! Check your email.');
        setSent(true);
        setStep(2);
        // For demo purposes, show the code if returned (in production, this would only be sent via email)
        if (data.reset_code) {
          toast.info(`Demo Mode - Your reset code: ${data.reset_code}`, { duration: 10000 });
        }
      } else {
        toast.error(data.detail || 'Failed to send reset code');
      }
    } catch (error) {
      toast.error('Error requesting password reset');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (!resetCode) {
      toast.error('Please enter the reset code');
      return;
    }
    
    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API}/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          email,
          reset_code: resetCode,
          new_password: newPassword 
        })
      });

      const data = await response.json();
      
      if (response.ok) {
        toast.success('Password reset successful! Please login.');
        navigate('/login');
      } else {
        toast.error(data.detail || 'Failed to reset password');
      }
    } catch (error) {
      toast.error('Error resetting password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a1628] flex flex-col" data-testid="forgot-password-page">
      {/* Header */}
      <header className="border-b border-[#1a2d4a] bg-[#0d1d35]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#b8860b] flex items-center justify-center">
              <span className="text-white font-bold text-xl">B</span>
            </div>
            <div>
              <span className="text-white font-bold text-lg tracking-wide">BETHEREUM</span>
              <p className="text-[#b8860b] text-xs tracking-widest">PRIVATE WEALTH MANAGEMENT</p>
            </div>
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <Button 
            variant="ghost" 
            className="mb-6 text-gray-400 hover:text-white"
            onClick={() => navigate('/login')}
          >
            <ArrowLeft className="mr-2 h-4 w-4" /> Back to Login
          </Button>
          
          <Card className="bg-[#0d1d35] border-[#1a2d4a]">
            <CardHeader className="text-center pb-2">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#b8860b]/20 flex items-center justify-center">
                {step === 1 ? (
                  <Mail className="w-8 h-8 text-[#b8860b]" />
                ) : (
                  <KeyRound className="w-8 h-8 text-[#b8860b]" />
                )}
              </div>
              <CardTitle className="text-2xl text-white">
                {step === 1 ? 'Reset Password' : 'Enter New Password'}
              </CardTitle>
              <CardDescription className="text-gray-400">
                {step === 1 
                  ? 'Enter your email to receive a reset code' 
                  : 'Enter the code sent to your email and your new password'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {step === 1 ? (
                <form onSubmit={handleRequestReset} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-gray-300">Email Address</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="bg-[#1a2d4a] border-[#2a3d5a] text-white placeholder:text-gray-500"
                      data-testid="reset-email-input"
                    />
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full bg-[#b8860b] hover:bg-[#9a7209] text-white font-medium"
                    disabled={loading}
                    data-testid="send-reset-btn"
                  >
                    {loading ? 'Sending...' : 'Send Reset Code'}
                  </Button>
                </form>
              ) : (
                <form onSubmit={handleResetPassword} className="space-y-4">
                  <div className="p-3 bg-green-900/20 border border-green-500/30 rounded-lg mb-4">
                    <div className="flex items-center gap-2 text-green-400 text-sm">
                      <CheckCircle className="w-4 h-4" />
                      <span>Reset code sent to {email}</span>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="resetCode" className="text-gray-300">Reset Code</Label>
                    <Input
                      id="resetCode"
                      type="text"
                      placeholder="Enter 6-digit code"
                      value={resetCode}
                      onChange={(e) => setResetCode(e.target.value)}
                      required
                      className="bg-[#1a2d4a] border-[#2a3d5a] text-white placeholder:text-gray-500 text-center text-lg tracking-widest"
                      maxLength={6}
                      data-testid="reset-code-input"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="newPassword" className="text-gray-300">New Password</Label>
                    <Input
                      id="newPassword"
                      type="password"
                      placeholder="••••••••"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                      minLength={8}
                      className="bg-[#1a2d4a] border-[#2a3d5a] text-white placeholder:text-gray-500"
                      data-testid="new-password-input"
                    />
                    <p className="text-xs text-gray-500">Minimum 8 characters</p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword" className="text-gray-300">Confirm Password</Label>
                    <Input
                      id="confirmPassword"
                      type="password"
                      placeholder="••••••••"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      className="bg-[#1a2d4a] border-[#2a3d5a] text-white placeholder:text-gray-500"
                      data-testid="confirm-password-input"
                    />
                  </div>
                  
                  <Button 
                    type="submit" 
                    className="w-full bg-[#b8860b] hover:bg-[#9a7209] text-white font-medium"
                    disabled={loading}
                    data-testid="reset-password-btn"
                  >
                    {loading ? 'Resetting...' : 'Reset Password'}
                  </Button>
                  
                  <button
                    type="button"
                    onClick={() => setStep(1)}
                    className="w-full text-center text-sm text-gray-400 hover:text-white mt-2"
                  >
                    Didn't receive the code? Try again
                  </button>
                </form>
              )}
              
              <div className="mt-6 text-center text-sm">
                <span className="text-gray-500">Remember your password? </span>
                <button 
                  onClick={() => navigate('/login')} 
                  className="text-[#b8860b] hover:underline font-medium"
                >
                  Sign In
                </button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
